import StateObserver from 'src/StateObserver';
import { createPersistentEmitter } from 'src/lib/Emitter';
import { togglePopup } from 'src/state/ui';
import PopupMonitor from 'src/game/logic/PopupMonitor';
import { resetCancelledExact } from 'src/state/analytics';
import { trackDialogOpen, trackDialogClose } from 'src/lib/analytics/events';
import type { DialogOrigin } from 'src/lib/analytics/properties';
import type {
  PopupOpenArgs,
  PopupID,
  PopupCloseArgs,
} from 'src/game/components/popups/popupRegistry';
import { captureGenericError } from '../sentry';
import { assertNever } from 'src/replicant/utils';
import { GuestModeOriginFeature } from 'src/game/guest/guest.analytics.schema';
import { NativePromoOrigin } from 'src/replicant/native';
import { TreasureExpressOriginFeature } from 'src/replicant/treasureExpress/treasureExpress.analytics.schema';
import { SupportedOrigins } from 'src/game/shop/Helpers';
import { ChampionsLeagueOriginFeature } from 'src/replicant/championsLeague/championsLeague.analytics.schema';
import { SocialSubFeature } from 'src/replicant/social/social.ruleset';
import { shouldTrackDialogOpen } from './popupDialogOpenTracking';

export type PopupDialogOrigin = DialogOrigin;

function openPopupInternal(
  id: PopupID,
  {
    cb,
    additionalTracking,
    ...opts
  }: {
    cb?: (...args: any[]) => void;
    additionalTracking?: Record<string, any>;
    origin: PopupDialogOrigin | undefined;
  },
): unknown[] | void {
  const origin: PopupDialogOrigin | undefined = opts.origin;

  if ((opts as any).numberOfLines > 0)
    console.warn('DEPRECATION WARNING: numberOfLines no longer does anything.');

  if (PopupMonitor.isOpen(id)) {
    throw new Error(`Double open popup: '${id}'`);
  }

  StateObserver.dispatch(
    togglePopup({ id, enabled: true, opts, origin, additionalTracking }),
  );

  if (cb) {
    const emitter = createPersistentEmitter(({ ui }) => {
      const { togglePopup } = ui;
      if (!togglePopup) return null;
      if (togglePopup.id !== id) return null;
      if (togglePopup.enabled) return null;

      if (!togglePopup.opts.args) {
        captureGenericError(
          `No args array provided when closing popup: '${id}'.`,
          null,
        );
      }

      return togglePopup.opts.args || [];
    });

    const onTogglePopup = (args: any) => {
      if (args) {
        cb(...args);
        emitter.removeListener(onTogglePopup);
      }
    };

    emitter.addListener(onTogglePopup);
  }

  if (shouldTrackDialogOpen(id)) {
    trackDialogOpen({ id, ...(additionalTracking ?? {}), origin });
  }
}

//

export function openPopupPromise<TPopupID extends PopupID>(
  id: TPopupID,
  opts: PopupOpenArgs<TPopupID>,
): Promise<PopupCloseArgs<TPopupID>[0]> {
  if (!opts) {
    captureGenericError(
      `Falsy opts provided when opening popup: '${id}'.`,
      null,
    );
  }

  return new Promise((resolve) => {
    openPopupInternal(id, {
      ...opts,
      cb: (...args) => resolve(args?.[0]),
      origin: opts.origin,
      additionalTracking: opts.additionalTracking,
    });
  });
}

//

export function closePopup<TPopupID extends PopupID>(
  id: TPopupID,
  ...args: PopupCloseArgs<TPopupID>
) {
  closePopupWithTracking(id, {}, ...args);
}

export function closePopupWithTracking<TPopupID extends PopupID>(
  id: TPopupID,
  closeTracking: Record<string, any>,
  ...args: PopupCloseArgs<TPopupID>
) {
  if (!PopupMonitor.isOpen(id)) {
    throw new Error(`Double close popup: '${id}'`);
  }

  const togglePopupState = StateObserver.getState().ui.togglePopup;
  if (!togglePopupState) return;

  const { origin, additionalTracking } = togglePopupState;

  StateObserver.dispatch(
    togglePopup({ id, enabled: false, opts: { args }, origin }),
  );

  trackDialogClose({
    id,
    ...(additionalTracking ?? {}),
    ...closeTracking,
    origin,
  });
  resetPopupAnalytics(id);
}

function resetPopupAnalytics(id: PopupID) {
  StateObserver.dispatch(resetCancelledExact());
}

// This probably shouldn't keep working.
export function closeAllPopups() {
  for (const popup of PopupMonitor.getOpenPopups()) {
    closePopup(popup);
  }
}

type OriginFeature =
  | 'menu'
  | 'admin'
  | 'home-button'
  | 'tutorial'
  | 'settings'
  | TreasureExpressOriginFeature
  | ChampionsLeagueOriginFeature
  | SocialSubFeature
  | GuestModeOriginFeature
  | NativePromoOrigin
  | SupportedOrigins;

export function mapFeatureToDialogOrigin(
  originFeature: OriginFeature | undefined,
): PopupDialogOrigin | undefined {
  switch (originFeature) {
    case 'launch':
    case 'launchSequence':
    case 'match2_pregame':
    case 'invite_launch':
      return 'launch';

    case 'hud':
    case 'menu':
    case 'friendsMenu':
    case 'inviteSequence':
    case 'profile':
    case 'home_icon':
    case 'match2_bundle_sale':
    case 'home-button':
    case 'coins_shop_icon':
    case 'energy_shop_icon':
    case 'gem_shop_icon':
    case 'energy_plus_button':
    case 'gem_plus_button':
    case 'invite_icon':
      return 'hud';

    case 'postLevel':
    case 'postPlay':
    case 'invite_unlock':
      return 'postLevel';

    case 'match2':
    case 'decoration':
    case 'decor_progression':
    case 'gacha':
    case 'match2_team_create':
    case 'growthcapsule':
    case 'redo_evolution':
    case 'visit_revive':
    case 'low_gem_balance':
    case 'energy_minigame':
    case 'pet_progression':
      return 'pinch';

    case 'tutorialSequence':
    case 'purchase':
    case 'bpoints_from_puzzle':
    case 'admin':
    case 'tutorial':
    case 'catalog_fusion':
    case 'coins_plus_button':
    case 'energy_on_out_of_resources':
    case 'event_energy':
    case 'gems_on_out_of_resources':
    case 'slots':
    case 'arena':
    case 'ftue':
    case 'continue_social':
    case 'friends_search_players':
    case 'invite_event_energy':
    case 'invite_friends_toast':
    case 'invite_gems_icon':
    case 'invite_icon_energy_gift':
    case 'invite_instead_gems_shop':
    case 'invite_low_balance':
    case 'invite_match_level':
    case 'invite_new_pet':
    case 'invite_no_energy':
    case 'invite_pet_evolve':
    case 'invite_wallpaper_reward':
    case 'time_pressure_invite':
    case 'visit_no_friends_invite':
    case 'invite_buddies':
    case 'settings':
    case 'header':
    case undefined:
      return undefined;

    default:
      assertNever(originFeature);
  }
}
